import {
  ButtonDS,
  DialogModalDS,
  InputDS,
  NumericInputDS,
  PhotosInputDS,
  Spacer,
  Switch,
  TextCapitalized,
} from "@qivia/ui";
import { TextAreaDS } from "@qivia/ui/src/designSystem/components/Inputs/TextAreaInput";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useAppDispatch } from "../../../../../redux/hooks";
import { updateVehicleRestitutionAsync } from "../../fleetSlice";
import { ConfirmUpdateRestitutionModal } from "./ConfirmUpdateRestitutionModal";
import { SessionIdentity } from "./SessionIdentity";
import "../../../../../i18n/i18n";

export const UpdateRestitutionModal = (props: {
  visible: boolean;
  onClose: () => void;
  driversHistory: string;
  driverName: string;
  registrationNumber: string | null;
  departureLocation: string | null;
  furthestDestination: string | null;
  mileage: number;
  isCabinCleaned: boolean;
  isTrunkCleaned: boolean;
  observations: string | null;
  pictures: { uuid: string; url: string }[];
}) => {
  const { visible, onClose, driversHistory } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [mileage, setMileage] = useState<number | "">(props.mileage);
  const [isCabinCleaned, setIsCabinCleaned] = useState(props.isCabinCleaned);
  const [isTrunkCleaned, setIsTrunkCleaned] = useState(props.isTrunkCleaned);
  const [observations, setObservations] = useState(props.observations ?? "");
  const [keptPictures, setKeptPictures] = useState(props.pictures);
  const [newPhotos, setNewPhotos] = useState<File[]>([]);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

  const removeKeptPicture = useCallback(
    (uuid: string) =>
      setKeptPictures((pictures) =>
        pictures.filter((picture) => picture.uuid !== uuid),
      ),
    [],
  );

  const hasChanges = useMemo(
    () =>
      mileage !== props.mileage ||
      isCabinCleaned !== props.isCabinCleaned ||
      isTrunkCleaned !== props.isTrunkCleaned ||
      observations !== (props.observations ?? "") ||
      keptPictures.length !== props.pictures.length ||
      newPhotos.length > 0,
    [
      mileage,
      isCabinCleaned,
      isTrunkCleaned,
      observations,
      keptPictures,
      newPhotos,
      props.mileage,
      props.isCabinCleaned,
      props.isTrunkCleaned,
      props.observations,
      props.pictures,
    ],
  );

  const canSubmit = useMemo(
    () => mileage !== "" && hasChanges,
    [mileage, hasChanges],
  );

  const submit = useCallback(async () => {
    const picturesToUpload = await Promise.all(
      newPhotos.map(async (file) => {
        const buffer = await file.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        const fileBuffer = btoa(
          Array.from(bytes, (b) => String.fromCharCode(b)).join(""),
        );
        return { originalFileName: file.name, fileBuffer };
      }),
    );

    void dispatch(
      updateVehicleRestitutionAsync({
        driversHistory,
        mileage: Number(mileage),
        isCabinCleaned,
        isTrunkCleaned,
        observations: observations === "" ? null : observations,
        pictures: keptPictures.map((picture) => picture.uuid),
        picturesToUpload,
      }),
    );
    setIsConfirmationVisible(false);
    onClose();
  }, [
    dispatch,
    driversHistory,
    isCabinCleaned,
    isTrunkCleaned,
    keptPictures,
    mileage,
    newPhotos,
    observations,
    onClose,
  ]);

  return (
    <>
      <DialogModalDS
        visible={visible && !isConfirmationVisible}
        deactivateClickOutsideSidePanel={isConfirmationVisible}
        onClose={onClose}
        title={t(
          "fleet.vehicleSessions.sidepanel.updateRestitutionModal.title",
        )}
        width={40}
        bottomButtons={{
          leftButton: (
            <ButtonDS
              text={t(
                "fleet.vehicleSessions.sidepanel.updateRestitutionModal.cancel",
              )}
              format={"fill"}
              buttonType={"secondary"}
              onClick={onClose}
            />
          ),
          rightButton: (
            <ButtonDS
              text={t(
                "fleet.vehicleSessions.sidepanel.updateRestitutionModal.validate",
              )}
              format={"fill"}
              buttonType={"primary"}
              disabled={!canSubmit}
              onClick={() => canSubmit && setIsConfirmationVisible(true)}
            />
          ),
        }}
      >
        <StyledColumn>
          <SessionIdentity
            driverName={props.driverName}
            registrationNumber={props.registrationNumber}
          />
          <Spacer y={1.5} />
          <StyledRow>
            <InputDS
              label={t(
                "fleet.vehicleSessions.sidepanel.updateRestitutionModal.departureLocation",
              )}
              value={props.departureLocation ?? ""}
              isDisabled
            />
            <Spacer x={1} />
            <InputDS
              label={t(
                "fleet.vehicleSessions.sidepanel.updateRestitutionModal.furthestDestination",
              )}
              value={props.furthestDestination ?? ""}
              isDisabled
            />
          </StyledRow>
          <NumericInputDS
            label={t(
              "fleet.vehicleSessions.sidepanel.updateRestitutionModal.mileage",
            )}
            value={mileage}
            update={setMileage}
          />
          <Spacer y={1} />
          <StyledSectionTitle>
            <TextCapitalized>
              {t(
                "fleet.vehicleSessions.sidepanel.updateRestitutionModal.cleaning",
              )}
            </TextCapitalized>
          </StyledSectionTitle>
          <Spacer y={1} />
          <StyledRow>
            <StyledSwitchRow>
              <TextCapitalized>
                {t(
                  "fleet.vehicleSessions.sidepanel.updateRestitutionModal.isCabinCleaned",
                )}
              </TextCapitalized>
              <Switch
                checked={isCabinCleaned}
                onClick={() => setIsCabinCleaned((prev) => !prev)}
                size="S"
                color="GREEN"
              />
            </StyledSwitchRow>
            <Spacer x={2.5} />
            <StyledSwitchRow>
              <TextCapitalized>
                {t(
                  "fleet.vehicleSessions.sidepanel.updateRestitutionModal.isTrunkCleaned",
                )}
              </TextCapitalized>
              <Switch
                checked={isTrunkCleaned}
                onClick={() => setIsTrunkCleaned((prev) => !prev)}
                size="S"
                color="GREEN"
              />
            </StyledSwitchRow>
          </StyledRow>
          <Spacer y={2.5} />
          <TextCapitalized>
            {t(
              "fleet.vehicleSessions.sidepanel.updateRestitutionModal.observations",
            )}
          </TextCapitalized>
          <Spacer y={1} />
          <StyledTextArea>
            <TextAreaDS value={observations} $update={setObservations} />
          </StyledTextArea>
          <Spacer y={2.5} />
          <TextCapitalized>
            {t(
              "fleet.vehicleSessions.sidepanel.updateRestitutionModal.pictures",
            )}
          </TextCapitalized>
          <Spacer y={1} />
          <PhotosInputDS
            newPictures={{ photos: newPhotos, setPhotos: setNewPhotos }}
            oldPictures={{
              photos: keptPictures,
              onRemove: removeKeptPicture,
            }}
            canAdd
            canRemove
            withDeleteIcon
            withViewIcon
          />
          <Spacer y={2.5} />
          <StyledWarning>
            <TextCapitalized>
              {t(
                "fleet.vehicleSessions.sidepanel.updateRestitutionModal.irreversible",
              )}
            </TextCapitalized>
          </StyledWarning>
        </StyledColumn>
      </DialogModalDS>
      <ConfirmUpdateRestitutionModal
        visible={isConfirmationVisible}
        onClose={() => setIsConfirmationVisible(false)}
        onConfirm={() => void submit()}
        driverName={props.driverName}
        registrationNumber={props.registrationNumber}
      />
    </>
  );
};

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledSwitchRow = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
`;

const StyledSectionTitle = styled.div`
  display: flex;
  ${typographies["Body/M"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledTextArea = styled.div`
  width: 100%;
  & textarea {
    box-sizing: border-box;
    field-sizing: content;
    resize: none;
  }
`;

const StyledWarning = styled.div`
  display: flex;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
  display: flex;
  align-items: center;
  justify-content: center;
`;
